import React, { isValidElement } from "react";
import styled from "@emotion/styled";
import { decode } from "he";
import { filter, has, map, isArray, reduce, join, isEmpty, find } from "lodash";
import Select from "~/types/Select";
import SelectArray from "~/types/SelectArray";

import UserContent from "~/components/UserContent";

import ProductTableLink from "~/components/links/ProductTableLink";

import {
    formatEventDate,
    getDateOnly,
    dateIsPast,
} from "~/utilities/formatDates";

import {
    appendLocale,
    getEnglishSlug,
    sanitizeAndParseHTML,
} from "~/utilities/helpers";

const DeviceSkus = styled.div`
    display: flex;
    flex-direction: column;
    gap: 5px;
    margin: 5px 0;
`;

const TableCellWrapper = styled(UserContent)`
    margin: 3px;
`;

export const formatTaxonomy = (taxonomy, urlBase) => ({
    id: taxonomy.id,
    name: taxonomy.name,
    linkUrl: `${urlBase}${taxonomy.slug}/`,
    taxonomyDetails: taxonomy.taxonomyDetails,
});

export const formatArticleUrl = (basePath, slug, languageCode, translations) =>
    `${basePath}${getEnglishSlug(
        slug,
        languageCode,
        translations,
    )}${appendLocale(languageCode)}/`;

export const getKnowledgeCenterTopicUrl = (slug, getBasePath) =>
    `${getBasePath("knowledgeCenterBase")?.url}?topics=${slug}`;

export const getKnowledgeCenterTypeUrl = (slug, getBasePath) =>
    `${getBasePath("knowledgeCenterBase")?.url}?types=${slug}`;

export const getKnowledgeCenterProductLineUrl = (slug, getBasePath) =>
    `${getBasePath("knowledgeCenterBase")?.url}?categories=${slug}`;

export const getKnowledgeCenterLanguageUrl = (slug, getBasePath) =>
    `${getBasePath("knowledgeCenterBase")?.url}?language=${slug}`;

export const formatArticles = (articles, limit = -1, getBasePath) => {
    if (Array.isArray(articles)) {
        const formattedArticles = articles
            .filter((article) => !!article)
            .map(
                ({
                    id,
                    date,
                    slug,
                    title,
                    isComingSoonArticle,
                    featuredImage,
                    articleTopics,
                    articleTypes,
                    productLines,
                    primaryTopic,
                    details,
                    articleMetadata,
                    seo,
                    languageCode,
                    languageName,
                    language,
                    translations,
                    translationGroupId,
                }) => ({
                    id,
                    date,
                    author: getAuthorForKcCard(articleMetadata),
                    readingTime: seo?.readingTime,
                    topics: articleTopics.nodes.map(
                        ({ name, slug, taxonomyDetails }) => ({
                            name,
                            linkLabel: name,
                            linkUrl: getKnowledgeCenterTopicUrl(
                                slug,
                                getBasePath,
                            ),
                            slug,
                            taxonomyDetails,
                        }),
                    ),
                    types: articleTypes.nodes.map(
                        ({
                            name,
                            slug,
                            taxonomyDetails: { iconClass, translatedName },
                        }) => ({
                            linkLabel: name,
                            translatedName,
                            linkUrl: getKnowledgeCenterTypeUrl(
                                slug,
                                getBasePath,
                            ),
                            slug,
                            iconClass: iconClass,
                        }),
                    ),
                    productLines: productLines.nodes.map(
                        ({ link, name, slug }) => ({
                            linkLabel: name,
                            linkUrl: getKnowledgeCenterProductLineUrl(
                                slug,
                                getBasePath,
                            ),
                            slug,
                        }),
                    ),
                    primaryTopic: primaryTopic &&
                        has(primaryTopic, "primaryTopic") &&
                        !isEmpty(primaryTopic.primaryTopic) && {
                            name: primaryTopic.primaryTopic.name,
                            linkLabel: primaryTopic.primaryTopic.name,
                            linkUrl: getKnowledgeCenterTopicUrl(
                                primaryTopic.primaryTopic.slug,
                                getBasePath,
                            ),
                            slug: primaryTopic.primaryTopic.slug,
                        },
                    excerpt: details.cardCopy ? decode(details.cardCopy) : "",
                    image: featuredImage ? featuredImage.node : null,
                    title: decode(title),
                    url: formatArticleUrl(
                        getBasePath("knowledgeCenterArticle")?.url,
                        slug,
                        languageCode,
                        translations,
                    ),
                    gated: details.gated && !dateIsPast(details.gatedUntil),
                    urlOverride: details.urlOverride,
                    openUrlOverrideInANewTab: details.openUrlOverrideInANewTab,
                    comingSoon: details.comingSoon ?? false,
                    releaseDate: details.releaseDate ?? "",
                    ctaCopyOverride: details.ctaCopyOverride,
                    openFeatureInNewTab: details.openFeatureInNewTab,
                    videoUrl: details.videoUrl,
                    isComingSoonArticle,
                    languageCode,
                    languageName,
                    translationGroupId,
                    language,
                }),
            );

        if (limit !== -1) {
            return formattedArticles.slice(0, limit);
        }

        return formattedArticles;
    }

    return [];
};

const getAuthorForKcCard = (articleMetadata) => {
    const hostAuthor = articleMetadata?.hostAuthor;
    const guestAuthor = articleMetadata?.guestAuthor;

    if (isEmpty(hostAuthor) && isEmpty(guestAuthor)) {
        return null;
    }

    if (!isEmpty(hostAuthor)) {
        const mainHostAuthor = hostAuthor[0];
        const {
            authorAttributes: { authorName: name, jobTitle },
            featuredImage,
        } = mainHostAuthor;

        return { name, jobTitle, thumbnail: featuredImage?.node };
    }

    return guestAuthor[0];
};

const articleHasSelectedCategory = (newsCategories, selectedCategories) => {
    // If article has no categories
    if (!newsCategories?.nodes) {
        return false;
    }

    const articlesCategories = newsCategories.nodes;

    // Check each article's category against each selected category
    const categoryIncluded = articlesCategories.some((articleCategory) => {
        return selectedCategories.some(
            (selectedCategory) => selectedCategory.id === articleCategory.id,
        );
    });

    return categoryIncluded;
};

export const filterArticles = (
    articles,
    selectedCategories,
    categoryOptions,
) => {
    const filteredArticles = articles.filter(({ newsCategories }) => {
        // Check if article has the selected categories
        const categoryMatch = articleHasSelectedCategory(
            newsCategories,
            selectedCategories,
        );

        // If option is to hide selected category then return article if it does not have selected category
        if (categoryOptions === "hide") {
            return !categoryMatch;
        }

        return categoryMatch;
    });

    return filteredArticles;
};

export const formatFilterOptions = (articles) => {
    return articles.reduce((acc, { newsCategories }) => {
        const filtersToInclude = newsCategories.nodes.reduce(
            (categoryAcc, currentArticleCategory) => {
                const taxonomyDetails = currentArticleCategory.taxonomyDetails;

                if (taxonomyDetails?.hideFromFilter) {
                    return categoryAcc;
                }

                const translations = taxonomyDetails?.translatedName || [];

                return categoryAcc.concat({
                    label: currentArticleCategory.name,
                    id: currentArticleCategory.id,
                    translations,
                });
            },
            [],
        );

        if (
            acc.some((accCat) =>
                filtersToInclude.some((filter) => filter.id === accCat.id),
            )
        ) {
            return acc;
        }

        return acc.concat(filtersToInclude);
    }, []);
};

const getNewsArticleUrl = (slug, languageCode, translations, getBasePath) => {
    const baseUrl = getBasePath("newsBase")?.url || "/";

    if (languageCode === "en") {
        return `${baseUrl}${slug}`;
    }

    const enTranslation = find(translations, { languageCode: "en" });

    if (isEmpty(enTranslation)) {
        return `${baseUrl}${slug}`;
    }

    return `${baseUrl}${enTranslation.slug}/${languageCode}/`;
};

export const formatNewsArticle = (
    {
        __typename,
        id,
        featuredImage,
        newsCategories,
        newsTypes,
        languageCode,
        title,
        date,
        slug,
        translations,
        details: {
            cardSubtitle,
            urlOverride,
            ctaCopyOverride,
            openUrlOverrideInANewTab,
        },
    },
    withImage = false,
    getBasePath,
) => ({
    typename: __typename,
    id,
    title,
    date,
    slug,
    languageCode,
    translations,
    subtitle: cardSubtitle,
    categories: newsCategories.nodes.map((tax) =>
        formatTaxonomy(tax, getBasePath("newsCategory")?.url),
    ),
    image: withImage ? featuredImage?.node : null,
    types: newsTypes.nodes.map((tax) =>
        formatTaxonomy(tax, getBasePath("newsType")?.url),
    ),
    url: getNewsArticleUrl(slug, languageCode, translations, getBasePath),
    ctaCopyOverride,
    urlOverride,
    openUrlOverrideInANewTab,
});

export const formatArticleForEvents = ({ details, ...article }) => ({
    ...article,
    details,
    dates: {
        startDate: details.startDate,
        endDate: details.endDate,
    },
    eventData: {
        location: details.location,
        cardCopy: details.cardCopy,
        urlOverride: details.urlOverride,
    },
    eventTypes: {
        nodes: details.eventCategory ?? [],
    },
});

const getEventUrl = (
    eventData,
    slug,
    getBasePath,
    __typename,
    languageCode,
) => {
    if (!isEmpty(eventData.urlOverride)) {
        return eventData.urlOverride;
    }

    const kcBase = getBasePath("knowledgeCenterArticle")?.url;
    const evBase = getBasePath("eventsBase")?.url;
    const base = __typename === "WpArticle" ? kcBase : evBase;

    let url;

    if (languageCode === "en") {
        url = `${base}${slug}/`;
    } else {
        url = `${base}${slug}/${languageCode}/`;
    }

    return url;
};

export const formatEvent = (event, getBasePath) => {
    const {
        id,
        slug,
        title,
        dates,
        eventData,
        eventTypes,
        languageCode,
        __typename,
    } = event || {};

    return {
        typename: __typename,
        id,
        title,
        languageCode,
        date: formatEventDate(dates.startDate, dates.endDate),
        location: eventData.location,
        categories: eventTypes.nodes.map((cat) =>
            formatTaxonomy(cat, getBasePath("eventsCategory")?.url),
        ),
        url: getEventUrl(
            eventData,
            slug,
            getBasePath,
            __typename,
            languageCode,
        ),
        cardCopy: eventData.cardCopy,
    };
};

export const formatAttributes = (attributes) => {
    let formattedAttributes = [];
    if (isArray(attributes) && attributes.length) {
        const nonFalseyValues = filter(attributes, Boolean);
        formattedAttributes = map(nonFalseyValues, (attr) => {
            const attrArr = attr.split("_");

            const toCamelCase = attrArr.map((word, index) => {
                const lowerWord = word.toLowerCase();
                if (index === 0) {
                    return lowerWord;
                }

                return `${lowerWord.charAt(0).toUpperCase()}${lowerWord.slice(
                    1,
                )}`;
            });

            return toCamelCase.join("");
        });
    }

    return formattedAttributes;
};

export const getFormattedAttributes = (
    productAttributes,
    categoryAttributes,
) => {
    let formattedAttributes = [];
    if (isArray(productAttributes) && productAttributes.length) {
        formattedAttributes = formatAttributes(productAttributes);
    }

    if (!formattedAttributes.length) {
        formattedAttributes = formatAttributes(categoryAttributes);
    }

    return formattedAttributes;
};

export const formatAttributeValue = (attribute, columnName) => {
    let attributeToReturn;
    switch (columnName) {
        case "lowerDevices":
        case "upperDevices":
            if (Array.isArray(attribute)) {
                attributeToReturn = (
                    <>
                        {attribute.map((option) => {
                            const {
                                slug,
                                productUrl,
                                globalAttributes: {
                                    primaryCategory,
                                    sku,
                                    urlOverride,
                                },
                            } = option;
                            const url = productUrl || `${slug}`;
                            return (
                                <ProductTableLink
                                    {...{
                                        slug,
                                        urlOverride,
                                        primaryCategory,
                                        productUrl: url,
                                    }}
                                    key={sku}
                                    sortValue={sku}
                                    linkProduct={true}
                                    text={sku}
                                />
                            );
                        })}
                    </>
                );
            }

            break;
        case "processor":
            attributeToReturn = !attribute?.label ? (
                ""
            ) : attribute?.url ? (
                <ProductTableLink
                    sortValue={attribute.label}
                    linkProduct={true}
                    productUrl={attribute.url}
                    text={attribute.label}
                    newTab={attribute.target ? "newTab" : "default"}
                />
            ) : (
                attribute.label
            );
            break;
        case "deviceSkus":
            if (isArray(attribute) && !isEmpty(attribute)) {
                return (
                    <DeviceSkus>
                        {attribute.map((att) => {
                            return att?.url ? (
                                <ProductTableLink
                                    key={att.label}
                                    sortValue={att.label}
                                    linkProduct={true}
                                    productUrl={att.url}
                                    text={att.label}
                                    isDeviceSkus={true}
                                />
                            ) : (
                                att.label
                            );
                        })}
                    </DeviceSkus>
                );
            }
            break;
        default:
            if (attribute instanceof SelectArray) {
                attributeToReturn = join(
                    attribute.map((att) => att.label),
                    ", ",
                );
            } else {
                attributeToReturn = attribute;
            }
    }

    if (isValidElement(attributeToReturn)) {
        return attributeToReturn;
    }

    if (Array.isArray(attributeToReturn)) {
        return (
            <TableCellWrapper>
                {sanitizeAndParseHTML(attributeToReturn.join(", "))}
            </TableCellWrapper>
        );
    }

    return (
        <TableCellWrapper>
            {sanitizeAndParseHTML(attributeToReturn)}
        </TableCellWrapper>
    );
};

export const formatAttributeValueForSorting = (attribute) => {
    if (attribute instanceof SelectArray) {
        return join(
            attribute.map((att) => att.label),
            ", ",
        );
    }

    if (isValidElement(attribute)) {
        return attribute.props.sortValue ?? "";
    }

    if (typeof attribute === "boolean") {
        return attribute ? "Yes" : "No";
    }

    if (Array.isArray(attribute)) {
        return attribute.join(", ");
    }

    return attribute ?? "";
};

export const formatFilterableAttributes = (filterableAttributes) => {
    return reduce(
        filterableAttributes,
        (result, { name, value, type, options }) => {
            const formattedName = toCamelCase(name);

            switch (type) {
                case "select":
                    result[formattedName] = new SelectArray();
                    options.forEach((option) =>
                        result[formattedName].push(new Select(option)),
                    );
                    break;
                case "true_false":
                    result[formattedName] = value ? "Yes" : "No";
                    break;
                default:
                    result[formattedName] = value;
            }

            return result;
        },
        {},
    );
};

export const getCorrectAttributes = (primaryCategory, powerAttributes) => {
    if (!primaryCategory || primaryCategory === "rf") return {};
    return {
        ...powerAttributes,
        automotiveQualified: !!powerAttributes.automotiveQualified,
    };
};

const getCorrectPath = (path, url) => {
    if (path.endsWith(".pdf")) {
        return url;
    }

    return path;
};

export const formatMenuItems = (menu) => {
    if (
        !has(menu, "nodes") ||
        (has(menu, "nodes") && menu.nodes.length === 0)
    ) {
        return [];
    }

    return map(menu.nodes, ({ id, label, path, url }) => ({
        id,
        linkLabel: decode(label),
        linkUrl: getCorrectPath(path, url),
    }));
};

export const combineEventsAndArticles = (
    events = [],
    articles = [],
    getBasePath,
    sortOrder = "ASC",
    past = false,
) => {
    const formattedArticlesAsEvents = articles.map((article) =>
        formatArticleForEvents(article),
    );
    const combinedEvents = events.concat(formattedArticlesAsEvents);

    combinedEvents.sort((a, b) => {
        const aStart = new Date(a.dates.startDate);
        const bStart = new Date(b.dates.startDate);

        return sortOrder === "ASC" ? aStart - bStart : bStart - aStart;
    });

    return combinedEvents.reduce((acc, event) => {
        const end = getDateOnly(new Date(event.dates.endDate));
        const today = getDateOnly(new Date());
        const typeOfEventsToInclude = past ? today >= end : today <= end;

        if (typeOfEventsToInclude) {
            return acc.concat(formatEvent(event, getBasePath));
        }

        return acc;
    }, []);
};

export const toCamelCase = (str) => {
    let newStr = "";
    if (str) {
        let wordArr = str.split(/[-_]/g);
        for (let i in wordArr) {
            if (i > 0) {
                newStr +=
                    wordArr[i].charAt(0).toUpperCase() +
                    wordArr[i].slice(1).toLowerCase();
            } else {
                newStr += wordArr[i];
            }
        }
    } else {
        return newStr;
    }
    return newStr;
};
